import { useEditor } from "@craftjs/core";
import hoistNonReactStatics from "hoist-non-react-statics";
import { createElement, forwardRef } from "react";
import type { Viewport } from "src/types";
import {
  css,
  Interpolation,
  StyledComponent,
  ThemedStyledProps,
} from "styled-components";

import useViewportContext from "@/hooks/use-viewport-context";
import { LAYOUT } from "@/utils/constants";

// Indirectly import styled-components so that babel-plugin-styled-component will not perform its transformations
import { _styled } from "./_styled";

// We don't need to add desktop-specific styles since it will be the base css rules
type StyleableViewport = Exclude<Viewport, "desktop">;

type BaseProps<
  T extends React.ElementType,
  Props extends Record<string, any> = null
> = Props extends null
  ? React.ComponentProps<T>
  : React.ComponentProps<T> & Props;

type InnerProps<T extends React.ElementType, Props> = ThemedStyledProps<
  BaseProps<T, Props>,
  any
> & {
  $viewport: {
    [V in StyleableViewport]: (
      ...args: CSSMethodArgs<T, Props>
    ) => ReturnType<typeof css>;
  };
};

type CSSMethodArgs<T extends React.ElementType, Props> = [
  style: Parameters<typeof css>[0],
  ...args: Interpolation<InnerProps<T, Props>>[]
];

type StyledComponentConfig = {
  /**
   * @internal
   * @hidden
   *
   * Sets the internal styled-component's displayName
   * Do not manually set this, it'll be overriden by babel-plugin-with-viewport-style
   */
  displayName: string;
  /**
   * @internal
   * @hidden
   *
   * Sets the internal styled-component's componentId
   * Do not manually set this, it'll be overriden by babel-plugin-with-viewport-style
   */
  componentId: string;
};

export const withViewportStyle = <T extends React.ElementType>(
  comp: T,
  config: Partial<StyledComponentConfig> = {}
) => {
  return <Props>(...args: CSSMethodArgs<T, Props>) => {
    const StyledComp = _styled(comp).withConfig(config as any)`
      ${css(...args)}
    `;

    const createViewportRule = (type: StyleableViewport) => {
      return (currentViewportType: Viewport, isEditing: boolean) => (
        ...args: CSSMethodArgs<T, Props>
      ) => {
        const rule = css(...args);

        // Use standard media queries when not editing
        if (!isEditing) {
          return css`
            @media screen and (max-width: ${type === "mobile"
                ? LAYOUT.MOBILE_MAX
                : LAYOUT.TABLET_MAX}) {
              ${rule}
            }
          `;
        }

        // Check if the specified viewport rules should be accepted by the currentViewport
        // Ie: if the current viewport is mobile, we should accept rules from both mobile and tablet
        const shouldIncludeRule =
          currentViewportType === "mobile"
            ? ["tablet", "mobile"].includes(type)
            : currentViewportType === type;

        if (shouldIncludeRule) {
          return rule;
        }

        return null;
      };
    };

    const WrappedComp = forwardRef((props: BaseProps<T, Props>, ref) => {
      const { viewport: currentViewport } = useViewportContext();
      const { isEditing } = useEditor((state) => ({
        isEditing: state.options.enabled,
      }));

      return createElement(StyledComp, {
        ...props,
        ref,
        $viewport: {
          tablet: createViewportRule("tablet")(currentViewport, isEditing),
          mobile: createViewportRule("mobile")(currentViewport, isEditing),
        },
      });
    });

    hoistNonReactStatics(WrappedComp, StyledComp);
    WrappedComp.displayName = StyledComp.displayName;

    // Need to typecast so that TS won't throw an error when this component is
    // used inside another StyledComponent
    return (WrappedComp as unknown) as StyledComponent<
      T,
      any,
      BaseProps<T, Props>,
      never
    >;
  };
};
